import { Component, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, forkJoin, of, Subscription} from 'rxjs';
import {EmployeeService} from '../../../core/auth/_services/employee.service';
import {Employee} from '../../../core/models/employee/employee.model';
import {Store} from '@ngrx/store';
import {cloneDeep} from 'lodash-es';
import {CoreState, getRedirectedUrl, selectStandaloneState} from '../../../core/store';
import {AuthenticatedAction, LoadedAction} from '../../../core/store/user';
import {filter, switchMap} from 'rxjs/operators';
import {EppRouterService} from '../../../core/router/epp-router.service';
import {OktaAuthStateService} from '@okta/okta-angular';
import {Router} from '@angular/router';
import {OktaEmployeeModel} from '../../../core/auth/_models/okta-employee.model';
import {TargetEmployeeModel} from '../../../core/auth/_models/target-employee.model';
import {EForceEmployeeModel} from '../../../core/auth/_models/eforce-employee.model';
import {EmployeeConnectionInfoRequest} from '../../../core/models/employee/employee-connection-info-request.model';
import {StandaloneModeService} from '../../../core/standalone-mode.service';
import { AuthState } from 'node_modules/@okta/okta-auth-js/types/lib/core/types/AuthState';
import { TranslationService } from '../../../modules/i18n/translation.service';
import { HttpClient } from '@angular/common/http';
import { Country } from 'src/app/core/models/referential/country.model';

@Component({
    standalone: false,
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
    private unsubscribe: Subscription[] = [];
    private employee: Employee;

    constructor(
        private oktaAuthStateService: OktaAuthStateService,
        private store: Store<CoreState>,
        private eppRouterService: EppRouterService,
        private employeeService: EmployeeService,
        private router: Router,
        private readonly standaloneModeService: StandaloneModeService,
        private readonly translationService: TranslationService,
        private readonly httpClient: HttpClient
    ) {
    }

    ngOnInit() {
        this.unsubscribe.push(this.oktaAuthStateService.authState$
            .pipe(
                filter(authState => !!authState?.isAuthenticated),
                switchMap((payload: AuthState) => combineLatest([
                    this.employeeService.getEmployeeInfo(payload.accessToken.claims.employeeNumber.toString()),
                    this.httpClient.get<Country>(`/apiParams/v1/country/${payload.accessToken.claims.country}`),
                    this.store.select(getRedirectedUrl),
                    of(new OktaEmployeeModel(payload.accessToken.claims))
                ])),
                switchMap(([ employeeInfo, country, redirectedUrl, oktaEmployee]) => {
                    const locale = country.locales?.find(l => l.isDefault)?.locale;
                    localStorage.setItem('isCrmLinked', String(country.hasCrm));
                    if (!employeeInfo.locale) {
                        return this.employeeService.saveEmployeeConnectionInfo(new EmployeeConnectionInfoRequest(employeeInfo.employeeNumber, locale)).pipe(
                            switchMap(() => {
                                employeeInfo.locale = locale;
                                return of({ employeeInfo, redirectedUrl, oktaEmployee });
                            })
                        )
                    } else {
                        return of({ employeeInfo, redirectedUrl, oktaEmployee });
                    }
                }),
                switchMap(({employeeInfo, redirectedUrl, oktaEmployee}) => {
                    const isUserAc = this.checkOfflineMode(employeeInfo);
                    this.updateEforceEmployeeWithOktaInfo(employeeInfo, oktaEmployee);
                    this.employee = this.employeeService.mapEmployee(employeeInfo);
                    return of({ employeeInfo, redirectedUrl, isUserAc });
                }),
                switchMap(({ employeeInfo, redirectedUrl, isUserAc }) => {           
                    return this.translationService.downloadAndUse(employeeInfo.locale).pipe(
                        switchMap(() => {
                            this.store.dispatch(new LoadedAction({ employee: cloneDeep(this.employee) }));
                            if (!isUserAc) {
                                return forkJoin([of(employeeInfo), of(true), of(redirectedUrl)]);
                            }
                            this.standaloneModeService.synchronizeStandaloneMode().subscribe();
                            return combineLatest([
                                of(employeeInfo),
                                this.store.select(selectStandaloneState).pipe(
                                    switchMap((standaloneState) => of(
                                        standaloneState.isFirstTimeSyncDone && 
                                        standaloneState.currentCmcVersion != null && 
                                        standaloneState.currentStandardVersion != null
                                    ))
                                ),
                                of(redirectedUrl)
                            ]);
                        })
                    );
                })
            ).subscribe(
                (([employeeInfo, isFirstTimeSyncDone, redirectedUrl]) => {
                    if (isFirstTimeSyncDone) {
                        this.store.dispatch(new AuthenticatedAction());
                        
                        if (this.employee?.employeeNumber !== employeeInfo.employeeNumber) {
                            if (redirectedUrl === '/standalone/opportunity-history') {
                                this.eppRouterService.redirectToMyOpportunities();
                            } else if (redirectedUrl === '/opportunity-history') {
                                this.eppRouterService.redirectToMyOpportunities();
                            } else {
                                this.eppRouterService.redirectToOpportunityContext(redirectedUrl);
                            }
                        } else {
                            this.eppRouterService.redirectToOpportunityContext(redirectedUrl);
                        }
                    }
                }),
                (error) => {
                    const errorCode = error?.error?.message === 'INVALID_EFORCE_EMPLOYEE' ? 422 : 404;
                    this.router.navigate(['/error/error-4', errorCode]);
                    console.error('Login error:', error);
                }
            ));
    }

    private checkOfflineMode(employeeInfo: TargetEmployeeModel | EForceEmployeeModel): boolean {
        const hasRoleAC = employeeInfo?.roles?.every(role => role.code === 'AC' || role.code === 'CRV_PRO') ?? false;
        localStorage.setItem('isStandaloneActive', String(hasRoleAC));
        return hasRoleAC;
    }

    private updateEforceEmployeeWithOktaInfo(employeeInfo, oktaEmployee) {
        employeeInfo.employeeNumber = oktaEmployee.employeeNumber;
        employeeInfo.firstName = oktaEmployee.firstName;
        employeeInfo.lastName = oktaEmployee.lastName;
        employeeInfo.email = oktaEmployee.email;
        employeeInfo.countryIsoCode = oktaEmployee.country;
    }

    ngOnDestroy() {
        this.unsubscribe.forEach((sb) => sb.unsubscribe());
    }
}
